import React, { useState, useEffect } from 'react';
import { formatValue, getSelectOptions } from './utils';
import EmptySection from './EmptySection';
import { useTranslations } from 'use-intl';
import { toTranslationKey, tForComponent } from '@/utils/translations';
import QuestionnaireInput from './QuestionnaireInput';
import { Typography } from '@/UIlibrary/Typography/Typography';
import { useUpdateQuestionnaireStore } from '@/stores/useUpdateQuestionnaire';
import {
	ActivityOrBusinessInformation as ActivityOrBusinessType,
	Maybe
} from '@/graphql/__generated__/credit-requests';
import QuestionnaireEditableInput from './QuestionnaireEditableInput';

interface ActivityOrBusinessInformationProps {
	data: Maybe<ActivityOrBusinessType> | undefined;
	title: string;
	isEditing: boolean;
	inputErrors?: Record<string, string>;
}

enum inputErrorMapping {
	activityType = 'activity_type',
	startDate = 'start_date',
	activityDetail = 'activity_detail'
}

const ActivityOrBusinessInformation: React.FC<ActivityOrBusinessInformationProps> = ({
	data,
	title,
	isEditing,
	inputErrors
}) => {
	const t = useTranslations('MicrocreditRequestQuestionnaire');
	const { activityOrBusiness, updateActivityOrBusiness } = useUpdateQuestionnaireStore();
	const [formData, setFormData] = useState(activityOrBusiness as ActivityOrBusinessType);

	useEffect(() => {
		setFormData(activityOrBusiness);
	}, [activityOrBusiness]);

	const handleChange = (key: string, value: any) => {
		setFormData((prevData: any) => ({
			...prevData,
			[key]: value
		}));
		updateActivityOrBusiness({ [key]: value });
	};

	const editableFields = () => {
		return (
			<>
				<div className="w-1/2 px-3 py-1">
					<QuestionnaireEditableInput
						title={'activityType'}
						value={formData['activityType'] || data?.activityType || ''}
						options={getSelectOptions('activityType', t)}
						isSelect={true}
						onChange={(newValue) => handleChange('activityType', newValue)}
						error={inputErrors?.[inputErrorMapping.activityType]}
					/>
				</div>
				<div className="w-1/2 px-3 py-1">
					<QuestionnaireEditableInput
						title={'startDate'}
						type="date"
						value={formData['startDate'] || data?.startDate || ''}
						onChange={(newValue) => handleChange('startDate', newValue)}
						error={inputErrors?.[inputErrorMapping.startDate] || ''}
					/>
				</div>
				<div className="w-1/2 px-3 py-1">
					<QuestionnaireEditableInput
						title={'activityDetail'}
						value={
							formData['activityDetail'] !== null
								? formData['activityDetail']
								: data?.activityDetail || ''
						}
						onChange={(newValue) => handleChange('activityDetail', newValue)}
						error={inputErrors?.[inputErrorMapping.activityDetail] || ''}
					/>
				</div>
			</>
		);
	};
	return (
		<div className="flex flex-col gap-1 p-4 bg-white rounded-lg w-full">
			<div className={`flex flex-row items-center mb-4`}>
				<div className="grow">
					<Typography weight="semibold">{t(toTranslationKey(title))}</Typography>
				</div>
			</div>
			{data ? (
				<div className="flex flex-row w-full">
					<div className="flex flex-row flex-wrap w-full">
						{isEditing ? (
							editableFields()
						) : (
							<>
								<div className="w-1/2 px-3 py-1">
									<QuestionnaireInput
										title={'activityType'}
										value={formatValue(data.activityType, t, 'activityType')}
									/>
								</div>
								<div className="w-1/2 px-3 py-1">
									<QuestionnaireInput
										title={'startDate'}
										value={formatValue(data.startDate, t, 'startDate')}
									/>
								</div>
								<div className="w-1/2 px-3 py-1">
									<QuestionnaireInput
										title={'activityDetail'}
										value={formatValue(data.activityDetail, t, 'activityDetail')}
									/>
								</div>
								<div className="w-1/2 px-3 py-1">
									<QuestionnaireInput
										title={'operatingTimeRange'}
										value={formatValue(data.operatingTimeRange, t, 'operatingTimeRange')}
									/>
								</div>
							</>
						)}
					</div>
				</div>
			) : (
				<EmptySection t={tForComponent(t)} />
			)}
		</div>
	);
};

export default ActivityOrBusinessInformation;
